<!-- contract-workflow-detail.component.html -->

<div class="cwd" *ngIf="!loading && task; else loadingTpl">

  <!-- ── Top Bar ─────────────────────────────────────────────── -->
  <div class="cwd__bar">
    <button class="cwd__back" (click)="router.navigate(['/workflow'])">
      <mat-icon>arrow_back</mat-icon>
    </button>

    <div class="cwd__meta">
      <span class="cwd__contract-num">{{ task.contractNumber }}</span>
      <span class="cwd__contract-title">{{ task.contractTitle }}</span>
    </div>

    <span class="cwd__stage-badge" [style.background]="stageBadgeColor">
      <mat-icon>{{ isReviewTask ? 'rate_review' : isApprovalTask ? 'approval' : isArchiveTask ? 'archive' : 'settings' }}</mat-icon>
      {{ task.taskType }}
    </span>

    <!-- Progress -->
    <div class="cwd__progress">
      <div class="cwd__progress-track">
        <div class="cwd__progress-fill" [style.width.%]="progressPct"></div>
      </div>
      <span class="cwd__progress-label">
        <ng-container *ngIf="isReviewTask">
          Reviewer {{ task.reviewStep + 1 }} / {{ task.reviewTotal }}
        </ng-container>
        <ng-container *ngIf="isApprovalTask">
          Signatory {{ task.approvalStep + 1 }} / {{ task.approvalTotal }}
        </ng-container>
      </span>
    </div>
  </div>

  <!-- ── Split Pane ──────────────────────────────────────────── -->
  <div class="cwd__split">

    <!-- LEFT: Document Panel (57%) -->
    <div class="cwd__doc-panel">

      <!-- Doc toolbar — shown for APPROVAL/ARCHIVE only; OO has its own toolbar -->
      <div class="cwd__doc-toolbar" *ngIf="!isReviewTask">
        <div class="cwd__zoom-controls">
          <button mat-icon-button (click)="zoomOut()"   title="Zoom out"><mat-icon>remove</mat-icon></button>
          <span class="cwd__zoom-level">{{ (zoomLevel * 100) | number:'1.0-0' }}%</span>
          <button mat-icon-button (click)="zoomIn()"    title="Zoom in"><mat-icon>add</mat-icon></button>
          <button mat-icon-button (click)="resetZoom()" title="Reset zoom"><mat-icon>fit_screen</mat-icon></button>
        </div>
      </div>

      <!-- Loading spinner -->
      <div *ngIf="docLoading" class="cwd__doc-loading">
        <mat-spinner diameter="32"></mat-spinner>
        <p>Loading document…</p>
      </div>

      <!-- REVIEW stage: OnlyOffice editor -->
      <div *ngIf="!docLoading && isReviewTask" class="cwd__oo-wrap">
        <div id="oo-container" class="cwd__oo-container"></div>
      </div>

      <!-- APPROVAL stage: PDF iframe -->
      <div *ngIf="isApprovalTask && task.pdfNodeId" class="cwd__pdf-wrap">
        <iframe #pdfFrame class="cwd__pdf-frame" [src]="safePdfUrl" title="Contract PDF"></iframe>
      </div>
      <div *ngIf="isApprovalTask && !task.pdfNodeId" class="cwd__doc-loading">
        <mat-icon style="font-size:48px;color:#c5d5ea">picture_as_pdf</mat-icon>
        <p>PDF being generated…</p>
      </div>

    </div>

    <!-- RIGHT: Task Panel (43%) -->
    <div class="cwd__task-panel">

      <!-- Participants — two columns side by side -->
      <div class="cwd__participants">
        <div class="cwd__participants-columns">

          <!-- Left: Review Sequence -->
          <div class="cwd__participants-col">
            <div class="cwd__participants-title">Review Sequence</div>
            <div class="cwd__participant-list">
              <ng-container *ngFor="let p of task.reviewers; let i = index">
                <div class="cwd__participant"
                     [class.cwd__participant--active]="normalizeStatus(p.status) === 'in_review'"
                     [class.cwd__participant--done]="normalizeStatus(p.status) === 'completed'">
                  <mat-icon class="cwd__participant-icon"
                            [style.color]="getParticipantColor(normalizeStatus(p.status))">
                    {{ getParticipantIcon(normalizeStatus(p.status)) }}
                  </mat-icon>
                  <div class="cwd__participant-info">
                    <span class="cwd__participant-name">{{ p.displayName }}</span>
                    <span class="cwd__participant-status">{{ getStatusLabel(p.status) }}</span>
                  </div>
                </div>
                <div *ngIf="i < task.reviewers.length - 1" class="cwd__participant-connector"></div>
              </ng-container>
              <div *ngIf="!task.reviewers?.length" class="cwd__participant-empty">No reviewers</div>
            </div>
          </div>

          <div class="cwd__participants-vdivider"></div>

          <!-- Right: Signing Sequence -->
          <div class="cwd__participants-col">
            <div class="cwd__participants-title">Signing Sequence</div>
            <div class="cwd__participant-list">
              <ng-container *ngFor="let p of task.approvers; let i = index">
                <div class="cwd__participant"
                     [class.cwd__participant--active]="normalizeStatus(p.status) === 'in_approval'"
                     [class.cwd__participant--done]="normalizeStatus(p.status) === 'completed'">
                  <mat-icon class="cwd__participant-icon"
                            [style.color]="getParticipantColor(normalizeStatus(p.status))">
                    {{ getParticipantIcon(normalizeStatus(p.status)) }}
                  </mat-icon>
                  <div class="cwd__participant-info">
                    <span class="cwd__participant-name">{{ p.displayName }}</span>
                    <span class="cwd__participant-status">{{ getStatusLabel(p.status) }}</span>
                  </div>
                </div>
                <div *ngIf="i < task.approvers.length - 1" class="cwd__participant-connector"></div>
              </ng-container>
              <div *ngIf="!task.approvers?.length" class="cwd__participant-empty">No signatories</div>
            </div>
          </div>

        </div>
      </div>

      <!-- ── Decision Form ────────────────────────────────────── -->
      <div class="cwd__decision">

        <!-- REVIEW task actions -->
        <ng-container *ngIf="isReviewTask">
          <div class="cwd__decision-title">Your Decision</div>

          <!-- Compact horizontal decision chips -->
          <div class="cwd__decision-chips">
            <label class="cwd__chip cwd__chip--approve" [class.selected]="decision === 'APPROVE'">
              <input type="radio" name="decision" value="APPROVE" [(ngModel)]="decision"/>
              <mat-icon>check_circle</mat-icon> Approve
            </label>
            <label class="cwd__chip cwd__chip--changes" [class.selected]="decision === 'REQUEST_CHANGES'">
              <input type="radio" name="decision" value="REQUEST_CHANGES" [(ngModel)]="decision"/>
              <mat-icon>edit</mat-icon> Changes
            </label>
            <label class="cwd__chip cwd__chip--reject" [class.selected]="decision === 'REJECT'">
              <input type="radio" name="decision" value="REJECT" [(ngModel)]="decision"/>
              <mat-icon>cancel</mat-icon> Reject
            </label>
            <!-- Forward toggle chip -->
            <label class="cwd__chip cwd__chip--forward"
                   [class.selected]="showForwardPanel"
                   (click)="showForwardPanel ? closeForwardPanel() : openForwardPanel()">
              <mat-icon>person_add</mat-icon> Add Reviewer
            </label>
          </div>

          <!-- ── Inline Forward Panel ─────────────────────────── -->
          <div class="cwd__inline-forward" *ngIf="showForwardPanel">

            <div class="cwd__inline-forward-hdr">
              <mat-icon>forward</mat-icon>
              <span>Forward for Review</span>
              <button class="cwd__inline-forward-close" type="button" (click)="closeForwardPanel()">
                <mat-icon>close</mat-icon>
              </button>
            </div>

            <!-- Search -->
            <div class="cwd__fwd-search-wrap">
              <mat-icon class="cwd__fwd-search-icon">search</mat-icon>
              <input class="cwd__fwd-search-input"
                     type="text"
                     placeholder="Search by name or username…"
                     [(ngModel)]="forwardSearch"
                     (input)="onForwardSearchChange()"
                     autocomplete="off"/>
              <mat-spinner *ngIf="forwardSearching" diameter="14"></mat-spinner>
            </div>

            <!-- Results dropdown -->
            <div class="cwd__fwd-results" *ngIf="forwardSearchResults.length > 0">
              <div class="cwd__fwd-result"
                   *ngFor="let u of forwardSearchResults"
                   (click)="addForwardUser(u)">
                <div class="cwd__fwd-avatar">{{ u.displayName?.charAt(0)?.toUpperCase() }}</div>
                <div class="cwd__fwd-result-info">
                  <span class="cwd__fwd-result-name">{{ u.displayName }}</span>
                  <span class="cwd__fwd-result-id">{{ u.id }}</span>
                </div>
                <mat-icon class="cwd__fwd-add-icon">add_circle_outline</mat-icon>
              </div>
            </div>

            <!-- No results -->
            <p class="cwd__fwd-no-results"
               *ngIf="forwardSearch.length >= 2 && !forwardSearching && forwardSearchResults.length === 0">
              No users found for "{{ forwardSearch }}"
            </p>

            <!-- Selected chips -->
            <div class="cwd__fwd-chips" *ngIf="forwardSelectedUsers.length > 0">
              <div class="cwd__fwd-chip" *ngFor="let u of forwardSelectedUsers">
                <span class="cwd__fwd-chip-av">{{ u.displayName?.charAt(0)?.toUpperCase() }}</span>
                <span class="cwd__fwd-chip-name">{{ u.displayName }}</span>
                <button class="cwd__fwd-chip-rm" type="button" (click)="removeForwardUser(u)">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>

            <!-- Note -->
            <textarea class="cwd__textarea cwd__textarea--sm"
                      [(ngModel)]="forwardComments"
                      rows="2"
                      placeholder="Note to reviewers (optional)…"></textarea>

            <!-- Error -->
            <div class="cwd__error" *ngIf="forwardError">
              <mat-icon>error</mat-icon> {{ forwardError }}
            </div>

            <!-- Actions -->
            <div class="cwd__fwd-actions">
              <button class="cwd__btn cwd__btn--ghost" type="button"
                      (click)="closeForwardPanel()" [disabled]="isForwarding">
                Cancel
              </button>
              <button class="cwd__btn cwd__btn--primary" type="button"
                      [disabled]="!forwardSelectedUsers.length || isForwarding"
                      (click)="submitForward()">
                <mat-spinner *ngIf="isForwarding" diameter="14"></mat-spinner>
                <mat-icon *ngIf="!isForwarding">send</mat-icon>
                {{ isForwarding ? 'Forwarding…' : 'Forward (' + forwardSelectedUsers.length + ')' }}
              </button>
            </div>

          </div><!-- /inline-forward -->

        </ng-container>

        <!-- APPROVAL task actions -->
        <ng-container *ngIf="isApprovalTask">
          <div class="cwd__decision-title">Sign or Reject</div>

          <button class="cwd__sign-btn"
                  [class.cwd__sign-btn--signed]="hasSigned"
                  [disabled]="signingInProgress"
                  (click)="signDocument()">
            <mat-spinner *ngIf="signingInProgress" diameter="18"></mat-spinner>
            <mat-icon *ngIf="!signingInProgress && !hasSigned">draw</mat-icon>
            <mat-icon *ngIf="!signingInProgress && hasSigned">check_circle</mat-icon>
            {{ hasSigned ? 'Signed — Click Complete below' : 'Sign Document' }}
          </button>

          <p class="cwd__sign-hint" *ngIf="!hasSigned">
            Click <strong>Sign Document</strong> to open the signing portal.
            After signing, return here and click <strong>Complete</strong>.
          </p>
          <p class="cwd__sign-hint cwd__sign-hint--success" *ngIf="hasSigned">
            <mat-icon>check</mat-icon>
            Document signed. The signed version has been saved to Alfresco.
            Click <strong>Complete</strong> to advance the workflow.
          </p>

          <div class="cwd__decision-chips" style="margin-top: 10px;">
            <label class="cwd__chip cwd__chip--reject" [class.selected]="decision === 'REJECT'">
              <input type="radio" name="decision" value="REJECT" [(ngModel)]="decision"/>
              <mat-icon>cancel</mat-icon> Reject Signing
            </label>
          </div>
        </ng-container>

        <!-- CONFIGURE task -->
        <ng-container *ngIf="isConfigureTask">
          <div class="cwd__decision-title">Confirm Routing</div>
          <p class="cwd__configure-hint">
            Review the reviewer and approver order below. Drag to reorder if needed,
            then click <strong>Submit for Review</strong>.
          </p>
        </ng-container>

        <!-- AMEND task -->
        <ng-container *ngIf="isAmendTask">
          <div class="cwd__decision-title">Resubmit After Changes</div>
          <p class="cwd__configure-hint">
            Edit the document using OnlyOffice, then click <strong>Resubmit for Review</strong>.
            The review cycle will restart from reviewer 1.
          </p>
        </ng-container>

        <!-- ARCHIVE task -->
        <ng-container *ngIf="isArchiveTask">
          <div class="cwd__decision-title">Archive Contract</div>
          <p class="cwd__configure-hint">
            The contract is fully executed. Click <strong>Archive Now</strong> to move it
            to the permanent archive folder.
          </p>
        </ng-container>

        <!-- Comments textarea — rejection / changes require explanation -->
        <div class="cwd__comments-field"
             *ngIf="decision === 'REJECT' || decision === 'REQUEST_CHANGES'">
          <label class="cwd__label">
            {{ decision === 'REJECT' ? 'Rejection reason' : 'Requested changes' }}
            <span class="cwd__required">*</span>
          </label>
          <textarea class="cwd__textarea"
                    [(ngModel)]="comments"
                    rows="3"
                    placeholder="Please describe the reason or required changes…"></textarea>
        </div>

        <!-- Optional comments for approve/sign -->
        <div class="cwd__comments-field"
             *ngIf="decision === 'APPROVE' || (isApprovalTask && hasSigned)">
          <label class="cwd__label">Comments (optional)</label>
          <textarea class="cwd__textarea" [(ngModel)]="comments" rows="2"
                    placeholder="Any notes for the record…"></textarea>
        </div>

        <!-- Error -->
        <div class="cwd__error" *ngIf="error">
          <mat-icon>error</mat-icon> {{ error }}
        </div>

        <!-- Submit button — hidden when forward panel is active -->
        <button class="cwd__submit-btn"
                *ngIf="!showForwardPanel"
                [class.cwd__submit-btn--danger]="decision === 'REJECT'"
                [class.cwd__submit-btn--warning]="decision === 'REQUEST_CHANGES'"
                [disabled]="!canSubmit || submitting"
                (click)="submitDecision()">
          <mat-spinner *ngIf="submitting" diameter="18"></mat-spinner>
          <ng-container *ngIf="!submitting">
            <ng-container [ngSwitch]="decision">
              <ng-container *ngSwitchCase="'APPROVE'">Approve Contract</ng-container>
              <ng-container *ngSwitchCase="'SIGN'">Complete</ng-container>
              <ng-container *ngSwitchCase="'REJECT'">Reject Contract</ng-container>
              <ng-container *ngSwitchCase="'REQUEST_CHANGES'">Request Changes</ng-container>
              <ng-container *ngSwitchCase="'SUBMIT'">Submit for Review</ng-container>
              <ng-container *ngSwitchCase="'ARCHIVE'">Archive Now</ng-container>
              <ng-container *ngSwitchDefault>Submit Decision</ng-container>
            </ng-container>
          </ng-container>
        </button>

      </div>

      <!-- ── Tabs ─────────────────────────────────────────────── -->
      <div class="cwd__tabs">
        <button class="cwd__tab" [class.active]="activeTab === 'comments'"   (click)="activeTab = 'comments'">
          <mat-icon>chat</mat-icon> Comments <span class="cwd__tab-count">{{ task.comments?.length }}</span>
        </button>
        <button class="cwd__tab" [class.active]="activeTab === 'metadata'"   (click)="activeTab = 'metadata'">
          <mat-icon>info</mat-icon> Details
        </button>
        <button class="cwd__tab" [class.active]="activeTab === 'signatures'" (click)="activeTab = 'signatures'"
                *ngIf="isApprovalTask">
          <mat-icon>draw</mat-icon> Signatures <span class="cwd__tab-count">{{ task.signatures?.length }}</span>
        </button>
      </div>

      <!-- Tab: Comments -->
      <div class="cwd__tab-content" *ngIf="activeTab === 'comments'">
        <div class="cwd__add-comment">
          <textarea class="cwd__textarea cwd__textarea--sm"
                    [(ngModel)]="newComment"
                    rows="2"
                    [placeholder]="replyingToId ? 'Replying…' : 'Add a comment…'"></textarea>
          <div class="cwd__comment-actions">
            <button *ngIf="replyingToId" class="cwd__btn cwd__btn--ghost" (click)="cancelReply()">Cancel</button>
            <button class="cwd__btn cwd__btn--primary" [disabled]="!newComment.trim()" (click)="submitComment()">
              <mat-icon>send</mat-icon> Post
            </button>
          </div>
        </div>

        <div *ngFor="let c of task.comments; trackBy: trackByComment" class="cwd__comment">
          <div class="cwd__comment-header">
            <span class="cwd__comment-author">{{ c.authorName || c.authorUsername }}</span>
            <span class="cwd__comment-time">{{ formatDate(c.createdAt) }}</span>
          </div>
          <div class="cwd__comment-body">{{ c.content }}</div>
          <button class="cwd__reply-btn" (click)="replyTo(c)">
            <mat-icon>reply</mat-icon> Reply
          </button>
          <div *ngFor="let r of c.replies" class="cwd__comment cwd__comment--reply">
            <div class="cwd__comment-header">
              <span class="cwd__comment-author">{{ r.authorName || r.authorUsername }}</span>
              <span class="cwd__comment-time">{{ formatDate(r.createdAt) }}</span>
            </div>
            <div class="cwd__comment-body">{{ r.content }}</div>
          </div>
        </div>

        <p *ngIf="!task.comments?.length" class="cwd__empty-tab">No comments yet.</p>
      </div>

      <!-- Tab: Metadata -->
      <div class="cwd__tab-content cwd__metadata" *ngIf="activeTab === 'metadata'">
        <div class="cwd__meta-row"><span>Contract No.</span><strong>{{ task.contractNumber }}</strong></div>
        <div class="cwd__meta-row"><span>Type</span><strong>{{ task.contractType }}</strong></div>
        <div class="cwd__meta-row"><span>Supplier</span><strong>{{ task.supplierName }}</strong></div>
        <div class="cwd__meta-row"><span>Department</span><strong>{{ task.departmentName }}</strong></div>
        <div class="cwd__meta-row"><span>Status</span>
          <strong><span class="cwd__status-pill">{{ task.contractStatus }}</span></strong>
        </div>
        <div class="cwd__meta-row" *ngIf="task.contractAmount">
          <span>Value</span>
          <strong>{{ task.contractAmount | number }} {{ task.currency }}</strong>
        </div>
        <div class="cwd__meta-row"><span>Effective</span><strong>{{ formatDate(task.effectiveDate) }}</strong></div>
        <div class="cwd__meta-row"><span>Expiry</span><strong>{{ formatDate(task.expiryDate) }}</strong></div>
      </div>

      <!-- Tab: Signatures -->
      <div class="cwd__tab-content" *ngIf="activeTab === 'signatures'">
        <div *ngFor="let sig of task.signatures" class="cwd__sig-row">
          <mat-icon style="color:#28a745">verified</mat-icon>
          <div class="cwd__sig-info">
            <span class="cwd__sig-name">{{ sig.signerName }}</span>
            <span class="cwd__sig-date">Signed {{ formatDate(sig.signedAt) }}</span>
          </div>
          <span class="cwd__sig-order">#{{ sig.signOrder }}</span>
        </div>
        <p *ngIf="!task.signatures?.length" class="cwd__empty-tab">No signatures recorded yet.</p>
      </div>

    </div><!-- /task panel -->

  </div><!-- /split -->

</div><!-- /cwd -->

<!-- Loading template -->
<ng-template #loadingTpl>
  <div class="cwd__full-loading" *ngIf="loading">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading task…</p>
  </div>
  <div class="cwd__full-loading" *ngIf="!loading && !task">
    <mat-icon style="font-size:48px;color:#c5d5ea">assignment_late</mat-icon>
    <p>Task not found.</p>
    <button mat-button (click)="router.navigate(['/workflow'])">Back to Workflow</button>
  </div>
</ng-template>